import { Download, ShieldCheck } from "lucide-react";
import { useCallback, useState } from "react";
import { commandCenterApi } from "../../api/command-center.js";
import { InlineError, LoadingTable } from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { CbomMigrationDesk } from "./CbomMigrationDesk.js";
import { InventoryAffectedFindings } from "./InventoryAffectedFindings.js";
import { InventoryBriefing } from "./InventoryBriefing.js";
import { InventoryLandscape } from "./InventoryLandscape.js";
import { InventoryQueue } from "./InventoryQueue.js";
import type { InventorySelection } from "./inventory-selection.js";

export function InventoryPage({
  selectedScanId,
}: {
  selectedScanId?: string | undefined;
}) {
  const [selection, setSelection] = useState<InventorySelection | null>(null);
  const load = useCallback(
    (signal: AbortSignal) =>
      commandCenterApi.getInventoryOverview(selectedScanId, signal),
    [selectedScanId],
  );
  const overview = useApiResource(load);
  if (overview.isLoading && !overview.data)
    return <LoadingTable columns={4} rows={5} />;
  if (overview.error && !overview.data) {
    return <InlineError error={overview.error} onRetry={overview.refresh} />;
  }
  const data = overview.data;
  if (!data) return null;
  const { sbomInventory: sbom, pqcInventory: cbom } = data;
  const downloadHref = commandCenterApi.sbomDownloadHref(selectedScanId);

  return (
    <section className="page" aria-labelledby="inventory-heading">
      <InventoryBriefing
        selection={selection}
        sbomRows={sbom.rows}
        cbomRows={cbom.rows}
        onClose={() => setSelection(null)}
        findingsSlot={
          selection ? <InventoryAffectedFindings selection={selection} /> : null
        }
        cbomBodySlot={
          selection?.kind === "cbom"
            ? (activeTab) => (
                <CbomMigrationDesk
                  row={selection.row}
                  cbomRows={cbom.rows}
                  activeTab={activeTab}
                />
              )
            : null
        }
      />
      <div className="page-heading">
        <div>
          <span className="page-kicker">Supply chain command center</span>
          <h1 id="inventory-heading">
            Know what you ship—and what cryptography will break first.
          </h1>
          <p>
            Components, crypto use, and post-quantum migration signals are tied
            to the exact frozen source evidence that produced them.
          </p>
        </div>
        {sbom.available ? (
          <a className="button button--primary" href={downloadHref} download>
            <Download aria-hidden="true" size={16} /> Download CycloneDX
          </a>
        ) : null}
      </div>

      {(!sbom.complete || !cbom.complete) &&
      (sbom.available || cbom.available) ? (
        <div className="scope-banner scope-banner--warning" role="status">
          <ShieldCheck aria-hidden="true" size={16} />
          <span>
            <strong>Partial portfolio evidence.</strong> Valid receipted
            inventories are shown; missing legacy receipts remain explicit and
            are not counted as zero.
          </span>
        </div>
      ) : null}

      <InventoryLandscape sbom={sbom} cbom={cbom} onSelect={setSelection} />

      <div className="inventory-table-duo">
        <InventoryQueue
          sbom={sbom}
          cbom={cbom}
          selection={selection}
          onSelect={setSelection}
        />
      </div>

      <aside className="callout callout--info">
        <ShieldCheck aria-hidden="true" size={18} />
        <div>
          <strong>Evidence boundary</strong>
          <p>Static manifest evidence only; runtime use and reachability are not asserted.</p>
        </div>
      </aside>
    </section>
  );
}
